// DESIGN LOCKED: matches the reference Status table. Do not change layout, spacing, columns, or sort order without an explicit design request.

export type StatusCategory = 'outage' | 'degraded' | 'operational' | 'standby';

export interface StatusModelItem {
  id: string;
  provider: string;
  requests?: number;
  successRate?: string | number | null;
  p50Latency?: string | number | null;
  status?: string | null;
  hasTraffic?: boolean;
}

export interface StatusDesignConfig {
  gridTemplateDesktop: string;
  gridTemplateMobile: string;
  rowHeight: string;
  rowHeightPx: number;
  barCount: number;
  barWidth: string;
  barGap: string;
  barHeight: string;
  barRadius: string;
  logoChipSize: string;
  cardRadius: string;
  pillRadius: string;
  columns: readonly ['model', 'strip', 'ttft', 'uptime', 'status'];
  statusRank: Record<StatusCategory, number>;
  pillStyles: Record<StatusCategory, string>;
  barColors: {
    healthy: string;
    degraded: string;
    failed: string;
    noData: string;
  };
}

export const STATUS_DESIGN: StatusDesignConfig = {
  // One shared grid-template-columns constant for desktop:
  // Model (flex, min 220px) | Last 24 hours (flex, min 240px) | TTFT (fixed 90px) | Uptime (fixed 90px) | Status (fixed 120px)
  gridTemplateDesktop: 'minmax(220px, 1.2fr) minmax(240px, 1.4fr) 90px 90px 120px',

  // Mobile layout (< 768px): strip column hidden, table scrolls internally
  gridTemplateMobile: 'minmax(180px, 1fr) 85px 85px 115px',

  // Comfortable row height: about 64-72px (locked at 68px), content vertically centered, never wrapping
  rowHeight: '68px',
  rowHeightPx: 68,

  // 24-hour strip dimensions: 96 fifteen-minute buckets with small gaps
  barCount: 96,
  barWidth: '2.5px',
  barGap: '1px',
  barHeight: '24px',
  barRadius: '1px',

  // Rounded logo tile for provider icon
  logoChipSize: '32px',

  // Card & Corner Radii
  cardRadius: '1rem',
  pillRadius: '9999px',

  // Column order: Model | Last 24 hours | TTFT · 1h | Uptime | Status (LAST)
  columns: ['model', 'strip', 'ttft', 'uptime', 'status'] as const,

  // Status sorting priority: worst first (Outage, Degraded, Operational, Standby)
  statusRank: {
    outage: 0,
    degraded: 1,
    operational: 2,
    standby: 3,
  },

  // Status pill styles (tinted background, no inner dot, target WCAG contrast)
  pillStyles: {
    outage: 'bg-[#F4795C]/15 text-[#F4795C] border border-[#F4795C]/30',
    degraded: 'bg-[#F7C04A]/15 text-[#F7C04A] border border-[#F7C04A]/30',
    operational: 'bg-[#4ADE9A]/15 text-[#4ADE9A] border border-[#4ADE9A]/30',
    standby: 'bg-white/5 text-[var(--text-muted)] border border-white/10',
  },

  // Bar colors: green = healthy, amber = partial failures (>=95%), red = failed (>5%), neutral dark gray = no traffic
  barColors: {
    healthy: 'bg-[#4ADE9A] hover:opacity-90',
    degraded: 'bg-[#F7C04A] hover:opacity-90',
    failed: 'bg-[#F4795C] hover:opacity-90',
    noData: 'bg-white/10 hover:bg-white/20',
  },
};

/**
 * Returns numeric priority rank for sorting models worst-first.
 * Lower rank appears higher in the list.
 */
export function getStatusRank(model: { hasTraffic?: boolean; status?: string | null }): number {
  if (!model.hasTraffic) return STATUS_DESIGN.statusRank.standby;
  const s = (model.status || '').toLowerCase().trim();
  if (s === 'outage') return STATUS_DESIGN.statusRank.outage;
  if (s === 'degraded') return STATUS_DESIGN.statusRank.degraded;
  if (s === 'operational') return STATUS_DESIGN.statusRank.operational;
  return STATUS_DESIGN.statusRank.standby;
}

/**
 * Normalizes model status category string.
 */
export function getStatusCategory(model: { hasTraffic?: boolean; status?: string | null }): StatusCategory {
  if (!model.hasTraffic) return 'standby';
  const s = (model.status || '').toLowerCase().trim();
  if (s === 'outage') return 'outage';
  if (s === 'degraded') return 'degraded';
  if (s === 'operational') return 'operational';
  return 'standby';
}

/**
 * Format TTFT value cleanly on a single line with tabular numerals.
 */
export function formatTtftCell(raw: string | number | null | undefined): string {
  if (raw === null || raw === undefined || raw === '' || raw === '—') return '—';
  if (typeof raw === 'number') {
    if (raw <= 0) return '—';
    if (raw >= 1000) return `${(raw / 1000).toFixed(2)} s`;
    return `${Math.round(raw)} ms`;
  }
  const str = String(raw).trim();
  if (str === '' || str === 'null' || str === 'undefined' || str === '—') return '—';
  const num = parseFloat(str);
  if (!Number.isFinite(num)) return str;
  if (str.endsWith('ms') || str.endsWith('s')) return str;
  if (num >= 1000) return `${(num / 1000).toFixed(2)} s`;
  return `${Math.round(num)} ms`;
}

/**
 * Format Success/Uptime Rate on a single line with tabular numerals and 2 decimals.
 */
export function formatSuccessRateCell(raw: string | number | null | undefined): string {
  if (raw === null || raw === undefined || raw === '' || raw === '—') return '—';
  if (typeof raw === 'number') {
    if (raw < 0) return '—';
    return `${raw.toFixed(2)}%`;
  }
  const str = String(raw).trim();
  if (str === '' || str === 'null' || str === 'undefined' || str === '—') return '—';
  const num = parseFloat(str.replace('%', ''));
  if (!Number.isFinite(num)) return str;
  return `${num.toFixed(2)}%`;
}

function escapeHtml(str: string): string {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Render dense strip of 96 thin vertical bars (one per 15-minute bucket in 24 hours).
 */
export function renderStatusBars(model: StatusModelItem): string {
  const totalBars = STATUS_DESIGN.barCount;
  let html = '';
  const now = Date.now();

  for (let b = 0; b < totalBars; b++) {
    const minutesAgoStart = (totalBars - b) * 15;
    const minutesAgoEnd = (totalBars - 1 - b) * 15;
    const timeStart = new Date(now - minutesAgoStart * 60000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const timeEnd = new Date(now - minutesAgoEnd * 60000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const timeRange = `${timeStart} - ${timeEnd}`;

    let colorClass = STATUS_DESIGN.barColors.noData;
    let tooltipText = `${timeRange}: 0 requests (no traffic)`;

    if (model.hasTraffic) {
      const cat = getStatusCategory(model);
      const totalReq = model.requests || 1;
      const rateNum = typeof model.successRate === 'number'
        ? model.successRate
        : parseFloat(String(model.successRate || '100').replace('%', ''));

      const successReq = Math.round((rateNum / 100) * totalReq);
      const failedReq = Math.max(0, totalReq - successReq);

      if (cat === 'outage') {
        colorClass = STATUS_DESIGN.barColors.failed;
        tooltipText = `${timeRange}: Incident / Outage (${successReq} success / ${failedReq} failed, ${totalReq} total)`;
      } else if (cat === 'degraded') {
        colorClass = STATUS_DESIGN.barColors.degraded;
        tooltipText = `${timeRange}: Degraded (${successReq} success / ${failedReq} failed, ${totalReq} total)`;
      } else {
        colorClass = STATUS_DESIGN.barColors.healthy;
        tooltipText = `${timeRange}: Healthy (${successReq} success / ${failedReq} failed, ${totalReq} total)`;
      }
    }

    html += `<div class="${colorClass} w-[${STATUS_DESIGN.barWidth}] h-[${STATUS_DESIGN.barHeight}] rounded-[${STATUS_DESIGN.barRadius}] transition-colors duration-150 shrink-0 cursor-pointer" title="${escapeHtml(tooltipText)}"></div>`;
  }
  return html;
}

/**
 * Render locked table header row sharing exact grid template.
 */
export function renderStatusTableHeader(is7d = false): string {
  const timeLabel = is7d ? 'Last 7 days' : 'Last 24 hours';
  return `
    <div class="status-grid-header border-b border-[rgba(255,255,255,0.08)] bg-[rgba(22,15,11,0.95)] sticky top-0 z-10 text-xs text-[var(--text-muted)] font-mono uppercase tracking-wider font-medium select-none"
         style="display: grid; grid-template-columns: ${STATUS_DESIGN.gridTemplateDesktop}; height: 48px;"
         data-grid-cols="${STATUS_DESIGN.gridTemplateDesktop}"
         data-row-height="48px">
      <div class="pl-6 pr-4 text-left font-medium min-w-0 overflow-hidden whitespace-nowrap">Model</div>
      <div class="hidden md:block px-4 text-left font-medium min-w-0 overflow-hidden whitespace-nowrap">${timeLabel}</div>
      <div class="px-3 text-right font-medium whitespace-nowrap cursor-help" title="Time to First Token (median over last hour)">TTFT · 1h</div>
      <div class="px-3 text-right font-medium whitespace-nowrap cursor-help" title="Uptime percentage over the period">Uptime</div>
      <div class="pl-3 pr-6 text-right font-medium whitespace-nowrap">Status</div>
    </div>
  `;
}

/**
 * Render one locked data row.
 */
export function renderStatusRow(
  model: StatusModelItem,
  options: { getProviderIcon?: (provider: string, cls?: string) => string } = {}
): string {
  const cat = getStatusCategory(model);
  const statusLabel = cat.charAt(0).toUpperCase() + cat.slice(1);
  const providerIcon = options.getProviderIcon
    ? options.getProviderIcon(model.provider, 'w-4 h-4')
    : `<span class="text-xs font-mono font-bold text-[var(--text-primary)]">${model.provider.charAt(0)}</span>`;

  return `
    <div class="status-grid-row border-b border-[rgba(255,255,255,0.08)] last:border-b-0 hover:bg-[rgba(255,255,255,0.02)] transition-colors duration-150"
         style="display: grid; grid-template-columns: ${STATUS_DESIGN.gridTemplateDesktop}; min-height: 64px; height: ${STATUS_DESIGN.rowHeight};"
         data-grid-cols="${STATUS_DESIGN.gridTemplateDesktop}"
         data-row-height="${STATUS_DESIGN.rowHeight}"
         data-status="${cat}"
         data-model-id="${escapeHtml(model.id)}">
      <div class="pl-6 pr-4 min-w-0 flex items-center gap-3 overflow-hidden">
        <div class="w-8 h-8 rounded-lg bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] flex items-center justify-center shrink-0" title="${escapeHtml(model.provider)}">
          ${providerIcon}
        </div>
        <span class="font-mono font-semibold text-xs sm:text-sm text-[var(--text-primary)] hover:text-[var(--text-accent)] truncate transition-colors cursor-pointer select-all whitespace-nowrap" onclick="navigate('/models/${escapeHtml(model.id)}')" title="${escapeHtml(model.id)} (${escapeHtml(model.provider)})">
          ${escapeHtml(model.id)}
        </span>
      </div>
      <div class="hidden md:flex items-center gap-[1px] px-4 overflow-hidden min-w-0">
        ${renderStatusBars(model)}
      </div>
      <div class="px-3 text-right font-mono text-xs sm:text-sm tabular-nums text-[var(--text-secondary)] whitespace-nowrap">
        ${formatTtftCell(model.p50Latency)}
      </div>
      <div class="px-3 text-right font-mono text-xs sm:text-sm tabular-nums text-[var(--text-secondary)] whitespace-nowrap font-medium">
        ${formatSuccessRateCell(model.successRate)}
      </div>
      <div class="pl-3 pr-6 flex items-center justify-end whitespace-nowrap">
        <span class="inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-mono font-medium ${STATUS_DESIGN.pillStyles[cat]} shrink-0">
          ${statusLabel}
        </span>
      </div>
    </div>
  `;
}

/**
 * Sorts status model list: worst first (Outage, Degraded, Operational, Standby), then alphabetically by model id.
 */
export function sortStatusModels(modelsList: StatusModelItem[]): StatusModelItem[] {
  return [...modelsList].sort((a, b) => {
    const rankA = getStatusRank(a);
    const rankB = getStatusRank(b);
    if (rankA !== rankB) return rankA - rankB;
    return (a.id || '').localeCompare(b.id || '');
  });
}

/**
 * Renders the complete status table HTML card.
 */
export function renderStatusTableCard(
  modelsList: StatusModelItem[],
  options: {
    getProviderIcon?: (provider: string, cls?: string) => string;
    is7d?: boolean;
  } = {}
): string {
  const is7d = Boolean(options.is7d);
  const sorted = sortStatusModels(modelsList);

  const rowsHtml = sorted.length === 0
    ? `
      <div class="py-16 text-center text-[var(--text-muted)] text-sm font-sans flex items-center justify-center">
        No model status telemetry recorded yet.
      </div>
    `
    : sorted.map((m) => renderStatusRow(m, options)).join('');

  return `
    <div class="status-surface-card rounded-2xl border border-[rgba(255,255,255,0.08)] overflow-hidden shadow-lg">
      <!-- Card Header: Title + Toggle + Legends -->
      <div class="p-5 sm:p-6 border-b border-[rgba(255,255,255,0.08)] space-y-3">
        <div class="flex items-center justify-between gap-4">
          <h2 class="text-lg sm:text-xl font-bold text-[var(--text-primary)] tracking-tight">Models</h2>
          <div class="inline-flex items-center p-1 rounded-xl bg-black/40 border border-[rgba(255,255,255,0.08)] text-xs font-medium shrink-0">
            <button onclick="state.statusRange='24h'; render();" class="px-3 py-1.5 rounded-lg transition-all ${is7d ? 'text-[var(--text-muted)] hover:text-white' : 'bg-[#c87a51] text-white shadow-sm font-semibold'}">24 hours</button>
            <button onclick="state.statusRange='7d'; render();" class="px-3 py-1.5 rounded-lg transition-all ${is7d ? 'bg-[#c87a51] text-white shadow-sm font-semibold' : 'text-[var(--text-muted)] hover:text-white'}">7 days</button>
          </div>
        </div>
        <div class="space-y-1.5 text-xs text-[var(--text-secondary)] leading-relaxed">
          <p>Degraded means requests succeed, but the median wait for the first token over the last hour is above 8 seconds.</p>
          <p>Each bar is 15 minutes of real requests and health checks. Yellow means some calls failed but at least 95% were served; red means more than one in twenty failed. Hover a bar for the counts.</p>
        </div>
      </div>

      <!-- Main Status Table -->
      <div class="overflow-x-auto custom-scrollbar">
        <div class="min-w-[680px] md:min-w-0">
          ${renderStatusTableHeader(is7d)}
          <div class="divide-y divide-[rgba(255,255,255,0.08)]">
            ${rowsHtml}
          </div>
        </div>
      </div>
    </div>
  `;
}
